import { Autocomplete, Box, Popover } from '@mui/material';
import { Button, Label, Spinner, TextInput, Textarea } from 'flowbite-react';
import React, { ReactNode } from 'react';
import { IoIosArrowDown } from 'react-icons/io';
import { useMutation, useQueryClient } from 'react-query';
import Select, { SingleValue } from 'react-select';
import { Dropdown } from 'shared-utils';
import { moveToNextRound, skipToRound } from 'src/apis/application';
import { useToast } from 'src/context/ToastProvider';
import { ShortListCandidateInput } from 'types';

interface Props {
	userId?: string;
	userName?: string;
	currentRound: number;
	totalRounds: number;
	positionId: string;
	isLoading?: boolean;
	onSubmit?: (data: ShortListCandidateInput) => void;
	onFinish?: () => void;
	nextRoundLabel?: string;
	showMessageInput?: boolean;
}

const RecruiterCandidateSkipToRound = ({
	userId,
	userName,
	onSubmit,
	onFinish,
	isLoading,
	positionId,
	currentRound,
	totalRounds,
	nextRoundLabel,
	showMessageInput = true,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [round, setRound] = React.useState<number>();
	const [feedback, setFeedback] = React.useState('');

	const skipUserToRoundFn = useMutation(['skipToRound'], skipToRound, {
		onSuccess(data) {
			queryClient.refetchQueries({ queryKey: ['getRecommendationsForPosition'], exact: false });
			toast({
				title: data.nextRoundExists
					? `${userName} skip to ${round || 0}`
					: `${userName} successfully qualified all rounds`,
				type: 'success',
			});
			setRound(undefined);
			onFinish && onFinish();
		},
		onError(error: Error) {
			toast({
				title: `Error occured ${error.message}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		console.log('{ positionId, round, userId, message }:', {
			positionId,
			round,
			userId: userId ?? '',
			message: feedback,
		});
		if (onSubmit) onSubmit({ positionId, userId: userId ?? '' });
		else skipUserToRoundFn.mutate({ positionId, userId: userId ?? '', roundNumber: round || 0, message: feedback });
	};

	const nextRoundExists = currentRound < totalRounds;
	const isLastRound = currentRound === totalRounds;
	const nextRoundString =
		nextRoundLabel ||
		(currentRound != undefined
			? isLastRound
				? 'Qualified All Round'
				: `Round ${currentRound + 1}`
			: 'Move to next round');

	const newOptionsArray = new Array(totalRounds - currentRound + 1).fill(0);
	const options = newOptionsArray.map((el, index) => {
		if (newOptionsArray?.length === index + 1) {
			return { label: `Qualified All Rounds`, value: String(currentRound + 1 + index) };
		}
		return { label: `Round ${currentRound + 1 + index}`, value: String(currentRound + 1 + index) };
	});

	const isLastRoundSelected = (round || 0) === totalRounds + 1;

	return (
		<form onSubmit={handleSubmit}>
			<div className="pt-3">
				<div className="mb-[8px]">
					<Label>{'Select Stage '}</Label>
					<span className="text-[red]">*</span>
				</div>

				<Autocomplete
					isOptionEqualToValue={(option, value) => option.value === value.value}
					sx={{
						display: 'inline-block',
						width: '100%',
						'& input': {
							width: '100%',
							bgcolor: 'background.paper',
							color: theme => theme.palette.getContrastText(theme.palette.background.paper),
						},
						'&.Mui-expanded svg': {
							fill: 'hsl(0, 0%, 40%)',
						},
					}}
					onChange={(event, value) => {
						setRound(Number(value?.value || 0));
					}}
					id="custom-input-demo"
					options={options}
					renderInput={params => (
						<div ref={params.InputProps.ref}>
							<TextInput
								placeholder={nextRoundString}
								className="w-full [&_input]:bg-white "
								type="text"
								rightIcon={() => <IoIosArrowDown className="fill-gray-400" />}
								{...params.inputProps}
							/>
						</div>
					)}
					PaperComponent={({ children, ...props }) => (
						<Box
							sx={{
								borderRadius: '8px',
								marginTop: '8px',
								bgcolor: 'white',
								boxShadow: '0 0 0 1px hsla(0, 0%, 0%, 0.1),0 4px 11px hsla(0, 0%, 0%, 0.1)',
							}}
							{...props}
						>
							{children}
						</Box>
					)}
				/>

				{/* Recruiter Feedback */}
				{showMessageInput ? (
					<div className="mb-[8px] mt-[16px]">
						<div className="mb-[8px] flex justify-between items-end">
							<div>
								<Label htmlFor="message" value="Feedback" />
							</div>

							<span className="italic text-xs text-[#6B7280] ">{(feedback?.length || 0) + ' / ' + 640}</span>
						</div>
						<Textarea
							style={{
								fontFamily: "'Inter', sans-serif",
								borderRadius: '8px',
							}}
							className="min-h-[87px] bg-white"
							value={feedback}
							maxLength={640}
							id="message"
							name="message"
							placeholder="Write your message here..."
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFeedback(e.target.value)}
						/>
					</div>
				) : null}
			</div>

			<Button
				type="submit"
				className="w-full mt-[20px] bg-[#7777E7]"
				color="purplePlaced"
				disabled={!round || skipUserToRoundFn.isLoading || isLoading}
			>
				{skipUserToRoundFn.isLoading || isLoading ? (
					<Spinner />
				) : nextRoundExists ? (
					isLastRoundSelected ? (
						`Skip to Qualified All Rounds`
					) : (
						`Skip to Round ${round ? round : ''}`
					)
				) : (
					'Skip to last Round'
				)}
			</Button>
		</form>
	);
};

export default RecruiterCandidateSkipToRound;
